import { z } from 'zod';
import { jsonSchema } from './json';

export const paymentProcessorSchema = z.enum(['STRIPE']);

export const externalPaymentTypeSchema = z.enum([
  'PAYMENT_SUCCEEDED',
  'REFUND_CREATED',
  'REFUND_UPDATED',
  'REFUND_FAILED',
  'DISPUTE_CREATED',
  'DISPUTE_UPDATED',
  'DISPUTE_CLOSED',
  'DISPUTE_FUNDS_WITHDRAWN',
  'DISPUTE_FUNDS_REINSTATED',
  'UNSUPPORTED',
]);

export const externalPaymentProcessingStatusSchema = z.enum([
  'RECEIVED',
  'WAITING_FOR_CLAIM',
  'WAITING_FOR_PAYMENT',
  'APPLIED',
  'REVIEW_REQUIRED',
  'IGNORED',
  'PROCESSING_FAILED',
]);

export const externalPaymentClaimAllocationSchema = z
  .object({
    externalPaymentID: z.guid(),
    claimID: z.guid(),
    tariffID: z.guid().nullable(),
    debitID: z.guid().nullable(),
    allocatedAmount: z.string(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
  })
  .strict();

export const externalPaymentSchema = z
  .object({
    id: z.guid(),
    type: externalPaymentTypeSchema,
    encounterNumber: z.string().nullable(),
    paymentPurpose: z.string().nullable(),
    metadataSiteNumber: z.string().nullable(),
    sourceEncounterID: z.string().nullable(),
    sourcePhysicianID: z.string().nullable(),
    sourceConsultID: z.string().nullable(),
    matchedClaimID: z.guid().nullable(),
    siteID: z.guid().nullable(),
    payorID: z.guid().nullable(),
    amount: z.string().nullable(),
    currency: z.string().length(3).nullable(),
    creditID: z.guid().nullable(),
    processingStatus: externalPaymentProcessingStatusSchema,
    attemptCount: z.number().int().nonnegative(),
    lastAttemptAt: z.coerce.date().nullable(),
    processedAt: z.coerce.date().nullable(),
    reviewReason: z.string().nullable(),
    receivedAt: z.coerce.date(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    processor: paymentProcessorSchema,
    processorEventID: z.string(),
    processorEventType: z.string(),
    processorObjectID: z.string().nullable(),
    processorPaymentID: z.string().nullable(),
    livemode: z.boolean(),
    processorCreatedAt: z.coerce.date(),
    payloadHash: z.string().length(64),
    payloadExpiresAt: z.coerce.date(),
  })
  .strict();

export const externalPaymentDetailSchema = externalPaymentSchema
  .extend({
    payload: jsonSchema.nullable(),
    claimAllocations: z.array(externalPaymentClaimAllocationSchema),
    relatedEvents: z.array(
      externalPaymentSchema.pick({
        id: true,
        processorEventID: true,
        processorEventType: true,
        type: true,
        amount: true,
        processingStatus: true,
        reviewReason: true,
        processorCreatedAt: true,
        receivedAt: true,
      })
    ),
  })
  .strict();

export const externalPaymentLinkSummarySchema = externalPaymentSchema
  .pick({
    id: true,
    processor: true,
    processorPaymentID: true,
    amount: true,
    currency: true,
    livemode: true,
    processorCreatedAt: true,
  })
  .extend({
    processorPaymentID: z.string().min(1),
    dashboardURL: z.url(),
  })
  .strict();

export const claimExternalPaymentLinksQuerySchema = z
  .object({ claimID: z.guid() })
  .strict();

export const externalPaymentListQuerySchema = z
  .object({
    processingStatus: externalPaymentProcessingStatusSchema.optional(),
    processor: paymentProcessorSchema.optional(),
    siteID: z.guid().optional(),
    encounterNumber: z.string().min(1).optional(),
    from: z.iso.datetime({ offset: true }).optional(),
    to: z.iso.datetime({ offset: true }).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
  })
  .strict();

export const externalPaymentIDSchema = z
  .object({ externalPaymentID: z.guid() })
  .strict();

export type ExternalPayment = z.infer<typeof externalPaymentSchema>;
export type ExternalPaymentDetail = z.infer<typeof externalPaymentDetailSchema>;
export type ExternalPaymentLinkSummary = z.infer<
  typeof externalPaymentLinkSummarySchema
>;
export type ExternalPaymentListQuery = z.infer<
  typeof externalPaymentListQuerySchema
>;
